import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const UserContext = createContext();
const KNOWN_USERS_KEY = 'knownUsers';

export const useUserContext = () => useContext(UserContext);

const readJsonValue = (key, fallback) => {
  try {
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : fallback;
  } catch (error) {
    return fallback;
  }
};

const normalizeUserId = (userId) => userId.trim();

const getKnownUsersFromTimelineKeys = () => {
  const userIds = [];

  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (!key?.startsWith('timelines_')) continue;

    const userId = key.replace('timelines_', '').trim();
    if (userId && userId !== 'undefined') {
      userIds.push(userId);
    }
  }

  return userIds;
};

export const getStoredKnownUsers = () => {
  const savedKnownUsers = readJsonValue(KNOWN_USERS_KEY, []);
  const knownUsers = Array.isArray(savedKnownUsers) ? savedKnownUsers : [];
  const currentUser = readJsonValue('currentUser', null);
  const currentUserId = typeof currentUser?.id === 'string' ? currentUser.id : '';

  return [...new Set([
    ...knownUsers.filter(userId => typeof userId === 'string').map(normalizeUserId).filter(Boolean),
    ...getKnownUsersFromTimelineKeys(),
    normalizeUserId(currentUserId)
  ].filter(Boolean))].sort((a, b) => a.localeCompare(b));
};

export const UserProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(() => {
    return readJsonValue('currentUser', null);
  });

  const [knownUsers, setKnownUsers] = useState(getStoredKnownUsers);

  const [userTimelines, setUserTimelines] = useState(() => {
    return currentUser?.id ? readJsonValue(`timelines_${currentUser.id}`, []) : [];
  });

  const addKnownUser = useCallback((userId) => {
    const normalizedUserId = normalizeUserId(userId);
    if (!normalizedUserId) return;

    setKnownUsers(prev => {
      const nextKnownUsers = [...new Set([...prev, normalizedUserId])].sort((a, b) => a.localeCompare(b));
      localStorage.setItem(KNOWN_USERS_KEY, JSON.stringify(nextKnownUsers));
      return nextKnownUsers;
    });
  }, []);

  // Batch localStorage updates to prevent race conditions
  const updateLocalStorage = useCallback((userId, timelines) => {
    Promise.resolve().then(() => {
      localStorage.setItem(`timelines_${userId}`, JSON.stringify(timelines));
    });
  }, []);

  useEffect(() => {
    if (currentUser) {
      localStorage.setItem('currentUser', JSON.stringify(currentUser));
      addKnownUser(currentUser.id);
    }
  }, [addKnownUser, currentUser]);

  useEffect(() => {
    if (currentUser) {
      updateLocalStorage(currentUser.id, userTimelines);
    }
  }, [userTimelines, currentUser, updateLocalStorage]);

  const login = (userId) => {
    const normalizedUserId = normalizeUserId(userId);
    if (!normalizedUserId) return;

    addKnownUser(normalizedUserId);
    setCurrentUser({ id: normalizedUserId });
    setUserTimelines(readJsonValue(`timelines_${normalizedUserId}`, []));
  };

  const logout = () => {
    localStorage.removeItem('currentUser');
    setCurrentUser(null);
    setUserTimelines([]);
  };

  const createTimeline = (name) => {
    const newTimeline = {
      id: Date.now(),
      name,
      events: [],
      tags: [],
      createdAt: new Date().toISOString()
    };
    setUserTimelines(prev => [...prev, newTimeline]);
    return newTimeline.id;
  };

  const importTimeline = useCallback((timeline) => {
    const importedTimeline = {
      ...timeline,
      id: Date.now(),
      name: timeline.name?.trim() || 'Imported Timeline',
      events: Array.isArray(timeline.events) ? timeline.events : [],
      tags: Array.isArray(timeline.tags) ? timeline.tags : [],
      createdAt: timeline.createdAt || new Date().toISOString(),
      importedAt: new Date().toISOString()
    };

    setUserTimelines(prev => [...prev, importedTimeline]);
    return importedTimeline.id;
  }, []);

  const updateTimeline = useCallback((timelineId, updates) => {
    setUserTimelines(prev => {
      const newTimelines = prev.map(timeline => 
        timeline.id === timelineId ? { ...timeline, ...updates } : timeline
      );
      return newTimelines;
    });
  }, []);

  const deleteTimeline = (timelineId) => {
    setUserTimelines(prev => prev.filter(timeline => timeline.id !== timelineId));
  };

  return (
    <UserContext.Provider value={{
      currentUser,
      knownUsers,
      userTimelines,
      login,
      logout,
      createTimeline,
      importTimeline,
      updateTimeline,
      deleteTimeline
    }}>
      {children}
    </UserContext.Provider>
  );
}; 
